import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import pluralize from "pluralize";
import {
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalErrorText,
    StandardModalLabel,
    Toast,
    portalColors
} from "src/components";
import UsersIcon from "src/img/new/users.svg";
import { updateUserTagAssignments } from "src/scenes/EmployeeList/api/userTagsApi";

const ModalStack = styled.div`
    display: grid;
    gap: 1.4rem;
`;

const UsersSummary = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    width: fit-content;
    min-width: 14rem;
    margin-left: 10px;
    padding: 0.55rem .55em;
    border: 1px solid ${portalColors.inputBorder};
    border-radius: 12px;
    background: ${portalColors.surface};
    box-shadow: ${portalColors.inputShadow};
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 800;
`;

const UsersSummaryIcon = styled.div`
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: ${portalColors.surfaceSoft};
    box-shadow: 0px 3px 6px #00000029;

    img {
        width: 1.2rem;
        height: 1.2rem;
        object-fit: contain;
    }
`;

const PanelsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;

    @media (max-width: 760px) {
        grid-template-columns: 1fr;
    }
`;

const PanelBlock = styled.div`
    display: grid;
    gap: 0.7rem;
`;

const TagsPanel = styled.div`
    min-height: 17rem;
    max-height: 17rem;
    overflow-y: auto;
    padding: 0.75rem;
    border: 1px solid ${portalColors.inputBorder};
    border-radius: 8px;
    background: ${portalColors.surface};
    box-shadow: ${portalColors.inputShadow};
    box-sizing: border-box;
`;

const AvailableTagsWrap = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    align-items: flex-start;
    gap: 0.55rem;
`;

const AvailableTagButton = styled.button`
    min-height: 2rem;
    max-width: 100%;
    padding: 0.35rem 0.75rem;
    border: 1px solid ${portalColors.blue};
    border-radius: 7px;
    background: ${portalColors.surface};
    color: ${portalColors.blue};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.75rem;
    font-weight: 800;
    cursor: pointer;
    overflow-wrap: anywhere;

    &:hover {
        background: ${portalColors.blueSoft};
    }
`;

const AssignedTagsWrap = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    align-items: flex-start;
    gap: 0.55rem;
`;

const AssignedTagPill = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    max-width: 100%;
    min-height: 2rem;
    padding: 0.35rem 0.75rem;
    border: 0;
    border-radius: 7px;
    background: var(--calendar-dark-ink, #171A22);
    color: var(--calendar-dark-control-text, #FFFFFF);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.75rem;
    font-weight: 800;
    cursor: pointer;
    overflow-wrap: anywhere;
`;

const EmptyState = styled.div`
    padding: 1rem;
    color: ${portalColors.mutedText};
    font-weight: 600;
    text-align: center;
`;

const AssignUserTagsModal = (props) => {
    const {
        open,
        onClose,
        token,
        users,
        tags,
        onSaved,
        customNames
    } = props;
    const [selectedTagUuids, setSelectedTagUuids] = useState([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const tagUuidSet = useMemo(() => new Set(tags.map(tag => tag.uuid)), [tags]);
    const selectedUserUuids = useMemo(() => users.map(user => user.uuid), [users]);
    const selectedTags = tags.filter(tag => selectedTagUuids.includes(tag.uuid));
    const availableTags = tags.filter(tag => !selectedTagUuids.includes(tag.uuid));
    const selectedUserTypeLabel = customNames?.employees || "Employees";
    const selectedUsersLabel = `${users.length} ${pluralize(selectedUserTypeLabel, users.length)}`;

    useEffect(() => {
        if (!open) {
            return;
        }

        const assignedTagUuids = [];
        const assignedTagUuidSet = new Set();

        users.forEach(user => {
            const userTags = user.userTags || [];
            userTags.forEach(tag => {
                if (tagUuidSet.has(tag.uuid) && !assignedTagUuidSet.has(tag.uuid)) {
                    assignedTagUuidSet.add(tag.uuid);
                    assignedTagUuids.push(tag.uuid);
                }
            });
        });

        setSelectedTagUuids(assignedTagUuids);
        setError("");
    }, [open, tagUuidSet, users]);

    const addTag = (tagUuid) => {
        if (selectedTagUuids.includes(tagUuid)) {
            return;
        }
        setSelectedTagUuids([...selectedTagUuids, tagUuid]);
        setError("");
    };

    const removeTag = (tagUuid) => {
        setSelectedTagUuids(selectedTagUuids.filter(uuid => uuid !== tagUuid));
    };

    const handleSave = async () => {
        if (loading) {
            return;
        }

        setLoading(true);
        setError("");
        try {
            await updateUserTagAssignments(token, selectedUserUuids, selectedTagUuids);
            await onSaved();
            Toast.success("Tags updated successfully");
            onClose();
        } catch (apiError) {
            setError(apiError.getApiErrorDetails().message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <StandardModal
            open={open}
            onClose={onClose}
            title="Assign Tags to Users"
            description="Choose one or more tags to assign to the selected users. This helps group users for easy filtering and targeted actions across the Me Business platform."
            widthMode="compactTable"
            footer={(
                <StandardModalActionGroup $pushRight>
                    <StandardModalActionButton disabled={loading} onClick={onClose}>Cancel</StandardModalActionButton>
                    <StandardModalActionButton
                        $variant="primary"
                        disabled={loading || tags.length === 0}
                        onClick={handleSave}
                    >
                        {loading ? "Saving..." : "Save Tags"}
                    </StandardModalActionButton>
                </StandardModalActionGroup>
            )}
        >
            <ModalStack>
                <div>
                    <StandardModalLabel>Users Selected</StandardModalLabel>
                    <UsersSummary>
                        <UsersSummaryIcon>
                            <img src={UsersIcon} alt="" />
                        </UsersSummaryIcon>
                        <span>{selectedUsersLabel}</span>
                    </UsersSummary>
                </div>

                <PanelsGrid>
                    <PanelBlock>
                        <StandardModalLabel>{availableTags.length} Tags Available</StandardModalLabel>
                        <TagsPanel>
                            {availableTags.length === 0 && (
                                <EmptyState>No tags available</EmptyState>
                            )}
                            <AvailableTagsWrap>
                                {availableTags.map(tag => (
                                    <AvailableTagButton
                                        type="button"
                                        key={tag.uuid}
                                        onClick={() => addTag(tag.uuid)}
                                    >
                                        {tag.name}
                                    </AvailableTagButton>
                                ))}
                            </AvailableTagsWrap>
                        </TagsPanel>
                    </PanelBlock>

                    <PanelBlock>
                        <StandardModalLabel>{selectedTags.length} Assigned Tags</StandardModalLabel>
                        <TagsPanel>
                            {selectedTags.length === 0 && (
                                <EmptyState>No tags assigned</EmptyState>
                            )}
                            <AssignedTagsWrap>
                                {selectedTags.map(tag => (
                                    <AssignedTagPill
                                        type="button"
                                        key={tag.uuid}
                                        onClick={() => removeTag(tag.uuid)}
                                    >
                                        <span>{tag.name}</span>
                                        <span>x</span>
                                    </AssignedTagPill>
                                ))}
                            </AssignedTagsWrap>
                        </TagsPanel>
                    </PanelBlock>
                </PanelsGrid>

                {error && <StandardModalErrorText>{error}</StandardModalErrorText>}
            </ModalStack>
        </StandardModal>
    );
};

export default AssignUserTagsModal;
